import { useState, useEffect, useMemo } from "react";
import DashboardLayout from "../components/DashboardLayout";
import Modal from "../components/Modal";
import ActionMenu, { type ActionMenuItem } from "../components/ActionMenu";
import { mastersApi, apiErrorMessage, type ApiError } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/hooks/useAuth";
import { formatDate, localToday } from "@/lib/data";
import { Plus, Search, Pencil, Trash2, MapPin, Archive, ArchiveRestore, AlertTriangle, RefreshCw, X } from "lucide-react";

interface Venue {
  id: number;
  name: string;
  location: string | null;
  capacity: number | null;
  description?: string | null;
  is_active: boolean;
  created_at?: string;
  /** Usage, worked out by the server in the same query */
  events_count?: number;
  quotations_count?: number;
  upcoming_events_count?: number;
  next_event_date?: string | null;
  next_event_name?: string | null;
}

interface VenuePayload {
  name: string;
  location: string | null;
  capacity: number | null;
  description: string | null;
  is_active: boolean;
}

type View = "active" | "archived" | "all";

const fmtDay = (v?: string | null) => (v ? formatDate(`${v.slice(0, 10)}T00:00:00`) : "");
const usedCount = (v: Venue) => (v.events_count || 0) + (v.quotations_count || 0);

// Venue Form Component
function VenueForm({
  initialData,
  onSubmit,
  onCancel,
}: {
  initialData?: Venue | null;
  /** Throws on failure; the form then stays open and shows the server's message */
  onSubmit: (data: VenuePayload) => Promise<void>;
  onCancel: () => void;
}) {
  const [name, setName] = useState(initialData?.name || "");
  const [location, setLocation] = useState(initialData?.location || "");
  // Empty = not known; the server accepts an empty capacity
  const [capacity, setCapacity] = useState(initialData?.capacity ? String(initialData.capacity) : "");
  const [description, setDescription] = useState(initialData?.description || "");
  const [isActive, setIsActive] = useState(initialData?.is_active ?? true);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    setFieldErrors({});
    setSaving(true);
    try {
      await onSubmit({
        name: name.trim(),
        location: location.trim() || null,
        capacity: capacity.trim() === "" ? null : Number(capacity),
        description: description.trim() || null,
        is_active: isActive,
      });
    } catch (error) {
      setFormError(apiErrorMessage(error, "Couldn't save the venue"));
      setFieldErrors((error as ApiError)?.errors || {});
    } finally {
      setSaving(false);
    }
  };

  const inputClass = (field: string) =>
    `w-full h-10 px-3 rounded-xl border text-sm outline-none focus:ring-2 ${
      fieldErrors[field] ? "border-rose-300 bg-rose-50 focus:border-rose-400 focus:ring-rose-100" : "border-stone-200 focus:border-amber-400 focus:ring-amber-100"
    }`;
  const fieldError = (field: string) =>
    fieldErrors[field]?.[0] ? <p className="text-xs text-rose-600 mt-1">{fieldErrors[field][0]}</p> : null;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label htmlFor="venue-name" className="block text-xs font-medium text-stone-500 mb-1.5">Venue Name *</label>
        <input
          id="venue-name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g., Grand Ballroom"
          className={inputClass("name")}
          required
        />
        {fieldError("name")}
      </div>
      <div>
        <label htmlFor="venue-location" className="block text-xs font-medium text-stone-500 mb-1.5">Location</label>
        <input
          id="venue-location"
          type="text"
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          placeholder="e.g., Ground floor, east wing"
          className={inputClass("location")}
        />
        {fieldError("location")}
      </div>
      <div>
        <label htmlFor="venue-capacity" className="block text-xs font-medium text-stone-500 mb-1.5">Capacity (guests)</label>
        <input
          id="venue-capacity"
          type="number"
          inputMode="numeric"
          min="1"
          step="1"
          value={capacity}
          onChange={(e) => setCapacity(e.target.value)}
          placeholder="Leave empty if not known"
          className={inputClass("capacity")}
        />
        {fieldError("capacity")}
      </div>
      <div>
        <label htmlFor="venue-description" className="block text-xs font-medium text-stone-500 mb-1.5">Description</label>
        <textarea
          id="venue-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Add venue details..."
          rows={3}
          className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none resize-none"
        />
      </div>
      <label htmlFor="venue-active" className="flex items-start gap-3 min-h-10 cursor-pointer">
        <input
          type="checkbox"
          id="venue-active"
          checked={isActive}
          onChange={(e) => setIsActive(e.target.checked)}
          className="mt-0.5 w-5 h-5 rounded border-stone-300"
        />
        <span className="text-sm text-stone-700">
          Active
          <span className="block text-xs text-stone-500">Untick to archive: it's hidden when picking a venue, but stays on old events.</span>
        </span>
      </label>

      {formError && (
        <div role="alert" className="flex items-start gap-2 rounded-xl bg-red-50 border border-red-200 px-3 py-2.5 text-sm text-red-700">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>{formError}</span>
        </div>
      )}

      <div className="flex gap-3 pt-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="flex-1 h-11 px-4 rounded-xl bg-stone-100 text-stone-600 font-semibold text-sm hover:bg-stone-200 transition-colors border border-stone-200 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving}
          className="flex-1 h-11 px-4 rounded-xl text-white font-semibold text-sm gold-gradient hover:opacity-90 transition-opacity disabled:opacity-60"
        >
          {saving ? "Saving…" : initialData?.id ? "Update Venue" : "Create Venue"}
        </button>
      </div>
    </form>
  );
}

export default function VenuesPage() {
  const { showToast } = useToast();
  const { user } = useAuth();
  const isStaff = user?.role === 'staff';
  const today = localToday();

  const [venues, setVenues] = useState<Venue[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [view, setView] = useState<View>("active");

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingVenue, setEditingVenue] = useState<Venue | null>(null);

  const [deletingVenue, setDeletingVenue] = useState<Venue | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      const response = await mastersApi.venues.list({ includeArchived: true });
      // Paginated response: { data: { data: [...], current_page: 1, ... } }
      setVenues(response.data?.data || response.data || []);
      setLoadError(null);
    } catch (error) {
      setLoadError(apiErrorMessage(error, "Couldn't load venues"));
    } finally {
      setLoading(false);
    }
  };

  const counts = useMemo(() => ({
    active: venues.filter((v) => v.is_active).length,
    archived: venues.filter((v) => !v.is_active).length,
    all: venues.length,
  }), [venues]);

  // Newest first (the API already sends them that way)
  const filteredVenues = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return venues.filter((v) => {
      if (view === "active" && !v.is_active) return false;
      if (view === "archived" && v.is_active) return false;
      if (!q) return true;
      return v.name.toLowerCase().includes(q) || (v.location || "").toLowerCase().includes(q);
    });
  }, [venues, searchQuery, view]);

  // ─── Actions ─────────────────────────────────────────────────────────────

  const openEdit = (venue: Venue) => {
    if (isStaff) return;
    setEditingVenue(venue);
    setIsModalOpen(true);
  };
  const closeForm = () => { setIsModalOpen(false); setEditingVenue(null); };

  /** Throws on failure so the form stays open and shows the server's message */
  const handleFormSubmit = async (data: VenuePayload) => {
    if (editingVenue) {
      await mastersApi.venues.update(editingVenue.id, data);
      showToast("Venue updated", "success");
    } else {
      await mastersApi.venues.create(data);
      showToast("Venue created", "success");
    }
    closeForm();
    loadData(true);
  };

  const setArchived = async (venue: Venue, archived: boolean) => {
    setBusy(true);
    try {
      await mastersApi.venues.setActive(venue.id, !archived);
      setVenues((prev) => prev.map((v) => (v.id === venue.id ? { ...v, is_active: !archived } : v)));
      showToast(archived ? `${venue.name} archived. It stays on its events.` : `${venue.name} restored`, "success");
      setDeletingVenue(null);
      setDeleteError(null);
    } catch (error) {
      showToast(apiErrorMessage(error, "Couldn't update the venue"), "error");
    } finally {
      setBusy(false);
    }
  };

  const openDelete = (venue: Venue) => { setDeleteError(null); setDeletingVenue(venue); };
  const closeDelete = () => { if (!busy) { setDeletingVenue(null); setDeleteError(null); } };

  const handleDelete = async () => {
    if (!deletingVenue) return;
    setBusy(true);
    try {
      await mastersApi.venues.delete(deletingVenue.id);
      showToast(`${deletingVenue.name} deleted`, "success");
      setVenues((prev) => prev.filter((v) => v.id !== deletingVenue.id));
      setDeletingVenue(null);
    } catch (error) {
      // e.g. it's used on events: keep the dialog open, explain, and offer Archive
      setDeleteError(apiErrorMessage(error, "Couldn't delete the venue"));
    } finally {
      setBusy(false);
    }
  };

  const menuItems = (venue: Venue): ActionMenuItem[] => [
    { label: "Edit", icon: Pencil, onClick: () => openEdit(venue) },
    venue.is_active
      ? { label: "Archive", icon: Archive, separated: true, onClick: () => setArchived(venue, true) }
      : { label: "Restore", icon: ArchiveRestore, separated: true, onClick: () => setArchived(venue, false) },
    { label: "Delete…", icon: Trash2, danger: true, onClick: () => openDelete(venue) },
  ];

  // ─── Rendering helpers ───────────────────────────────────────────────────

  const statusPill = (venue: Venue) => (
    <span className={`shrink-0 text-xs px-2 py-0.5 rounded-full font-medium ${venue.is_active ? "bg-emerald-100 text-emerald-700" : "bg-stone-100 text-stone-500"}`}>
      {venue.is_active ? "Active" : "Archived"}
    </span>
  );

  const upcomingText = (venue: Venue) => {
    const n = venue.upcoming_events_count || 0;
    if (!n || !venue.next_event_date) return <span className="text-stone-400">No upcoming events</span>;
    const day = venue.next_event_date.slice(0, 10);
    return (
      <span>
        <span className="text-stone-700">{day <= today ? "On now" : fmtDay(day)}</span>
        {venue.next_event_name ? ` · ${venue.next_event_name}` : ""}
        {n > 1 && <span className="text-stone-400"> (+{n - 1} more)</span>}
      </span>
    );
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="h-7 w-32 rounded bg-stone-100 animate-pulse mb-2" />
        <div className="h-4 w-64 max-w-full rounded bg-stone-100 animate-pulse mb-6" />
        <div className="h-10 rounded-xl bg-stone-100 animate-pulse mb-4" />
        <div className="bg-white border border-stone-200 rounded-xl divide-y divide-stone-100" aria-label="Loading venues">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="p-4 flex gap-4 items-center">
              <div className="flex-1 space-y-2">
                <div className="h-4 w-1/3 rounded bg-stone-100 animate-pulse" />
                <div className="h-3 w-1/4 rounded bg-stone-100 animate-pulse" />
              </div>
              <div className="h-5 w-16 rounded-full bg-stone-100 animate-pulse" />
            </div>
          ))}
        </div>
      </DashboardLayout>
    );
  }

  if (loadError) {
    return (
      <DashboardLayout>
        <div className="bg-white border border-stone-200 rounded-xl p-10 text-center">
          <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
          <p className="font-semibold text-stone-800">Couldn't load venues</p>
          <p className="text-sm text-stone-500 mt-1">{loadError}</p>
          <button type="button" onClick={() => loadData()} className="mt-4 h-10 inline-flex items-center gap-2 px-4 rounded-xl border border-stone-200 text-sm font-semibold text-stone-700 hover:bg-stone-50">
            <RefreshCw className="w-4 h-4" /> Try again
          </button>
        </div>
      </DashboardLayout>
    );
  }

  const deletingUsed = deletingVenue ? usedCount(deletingVenue) : 0;

  return (
    <DashboardLayout>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-stone-800 mb-1">Venues</h1>
        <p className="text-sm text-stone-500">Halls and lawns you host events in. Archive a venue you no longer use.</p>
      </div>

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-2 mb-3">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
          <input
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search venues..."
            aria-label="Search venues"
            className="w-full h-10 pl-9 pr-4 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
          />
        </div>
        {!isStaff && (
          <button
            type="button"
            onClick={() => { setEditingVenue(null); setIsModalOpen(true); }}
            className="px-4 h-10 rounded-xl text-white font-semibold text-sm gold-gradient hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Venue
          </button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="flex gap-1 p-1 rounded-xl bg-stone-100 border border-stone-200" role="group" aria-label="Show">
          {([["active", "Active"], ["archived", "Archived"], ["all", "All"]] as [View, string][]).map(([value, label]) => (
            <button
              key={value}
              type="button"
              aria-pressed={view === value}
              onClick={() => setView(value)}
              className={`h-9 px-3.5 rounded-lg text-sm whitespace-nowrap transition-colors ${view === value ? "bg-white text-stone-900 font-semibold shadow-sm" : "text-stone-600 hover:text-stone-900"}`}
            >
              {label} <span className="text-xs text-stone-400">{counts[value]}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      {filteredVenues.length === 0 ? (
        <div className="text-center py-12 bg-stone-50 rounded-xl border border-stone-200">
          <MapPin className="w-12 h-12 text-stone-300 mx-auto mb-3" />
          {venues.length === 0 ? (
            <>
              <p className="text-stone-600 font-medium">No venues yet</p>
              {!isStaff && (
                <button type="button" onClick={() => { setEditingVenue(null); setIsModalOpen(true); }} className="mt-4 h-10 inline-flex items-center gap-2 px-4 rounded-xl text-white font-semibold text-sm gold-gradient hover:opacity-90">
                  <Plus className="w-4 h-4" /> Add Venue
                </button>
              )}
            </>
          ) : (
            <>
              <p className="text-stone-600 font-medium">{searchQuery ? "No venues match your search" : view === "archived" ? "No archived venues" : "No venues here"}</p>
              <button type="button" onClick={() => { setSearchQuery(""); setView("all"); }} className="mt-3 min-h-10 inline-flex items-center gap-1 text-sm font-semibold text-amber-700 hover:underline">
                <X className="w-4 h-4" /> Show all venues
              </button>
            </>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
          {/* Mobile cards */}
          <div className="md:hidden divide-y divide-stone-100">
            {filteredVenues.map((venue) => (
              <div key={venue.id} className={`p-4 ${venue.is_active ? "" : "opacity-80"}`}>
                <div className="flex items-start justify-between gap-3">
                  <button type="button" className="min-w-0 text-left" onClick={() => openEdit(venue)}>
                    <p className="font-semibold text-stone-800 truncate">{venue.name}</p>
                    <p className="text-xs text-stone-500 mt-0.5 truncate">
                      {[venue.location, venue.capacity ? `${venue.capacity} guests` : ""].filter(Boolean).join(" · ") || "No location or capacity yet"}
                    </p>
                  </button>
                  {statusPill(venue)}
                </div>
                <div className="mt-3 flex items-center justify-between gap-2">
                  <p className="text-sm text-stone-600 min-w-0 truncate">
                    <span className="text-xs text-stone-400">Next </span>
                    {upcomingText(venue)}
                  </p>
                  {!isStaff && <ActionMenu items={menuItems(venue)} label={`More actions for ${venue.name}`} />}
                </div>
              </div>
            ))}
          </div>

          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead className="bg-stone-50">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500">Name</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500">Location</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-stone-500">Capacity</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500">Next event</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-stone-500">Upcoming</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500">Status</th>
                  <th className="px-4 py-3"><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {filteredVenues.map((venue) => (
                  <tr
                    key={venue.id}
                    onClick={() => openEdit(venue)}
                    className={`${isStaff ? "" : "cursor-pointer"} hover:bg-amber-50/40 transition-colors ${venue.is_active ? "" : "text-stone-500"}`}
                  >
                    <td className="px-4 py-3 font-medium text-stone-800">{venue.name}</td>
                    <td className="px-4 py-3 text-sm text-stone-600">{venue.location || "-"}</td>
                    <td className="px-4 py-3 text-right text-sm tabular-nums">{venue.capacity || <span className="text-stone-400">-</span>}</td>
                    <td className="px-4 py-3 text-sm max-w-[280px] truncate">{upcomingText(venue)}</td>
                    <td className="px-4 py-3 text-right text-sm tabular-nums">{venue.upcoming_events_count || <span className="text-stone-400">0</span>}</td>
                    <td className="px-4 py-3">{statusPill(venue)}</td>
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      {!isStaff && (
                        <div className="flex justify-end">
                          <ActionMenu items={menuItems(venue)} label={`More actions for ${venue.name}`} />
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Edit/Create Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={closeForm}
        title={editingVenue ? "Edit Venue" : "Add New Venue"}
        size="md"
      >
        {isModalOpen && (
          <VenueForm
            key={editingVenue?.id ?? "new"}
            initialData={editingVenue}
            onSubmit={handleFormSubmit}
            onCancel={closeForm}
          />
        )}
      </Modal>

      {/* Delete / archive dialog */}
      {deletingVenue && (
        <Modal
          isOpen={!!deletingVenue}
          onClose={closeDelete}
          title={deletingUsed > 0 || deleteError ? `Can't delete ${deletingVenue.name}` : `Delete ${deletingVenue.name}?`}
          size="sm"
          footer={
            <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
              <button type="button" onClick={closeDelete} disabled={busy} className="h-11 px-4 rounded-xl border border-stone-300 text-sm font-semibold text-stone-600 hover:bg-stone-50">
                {deletingUsed > 0 || deleteError ? "Close" : "Keep venue"}
              </button>
              {deletingUsed > 0 || deleteError ? (
                deletingVenue.is_active && (
                  <button type="button" onClick={() => setArchived(deletingVenue, true)} disabled={busy} className="h-11 px-4 rounded-xl gold-gradient text-white text-sm font-semibold disabled:opacity-50 inline-flex items-center justify-center gap-2">
                    <Archive className="w-4 h-4" /> {busy ? "Archiving…" : "Archive instead"}
                  </button>
                )
              ) : (
                <button type="button" onClick={handleDelete} disabled={busy} className="h-11 px-4 rounded-xl bg-red-600 hover:bg-red-700 text-white text-sm font-semibold disabled:opacity-50">
                  {busy ? "Deleting…" : "Delete permanently"}
                </button>
              )}
            </div>
          }
        >
          <div className="space-y-3 text-sm text-stone-600">
            {deletingUsed > 0 || deleteError ? (
              <>
                <p className="flex items-start gap-2 rounded-lg bg-amber-50 text-amber-800 px-3 py-2">
                  <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>
                    {deleteError || (
                      <>
                        It's used on{" "}
                        {[
                          deletingVenue.events_count ? `${deletingVenue.events_count} event${deletingVenue.events_count === 1 ? "" : "s"}` : "",
                          deletingVenue.quotations_count ? `${deletingVenue.quotations_count} quotation${deletingVenue.quotations_count === 1 ? "" : "s"}` : "",
                        ].filter(Boolean).join(" and ")}
                        {(deletingVenue.upcoming_events_count || 0) > 0 ? `, including ${deletingVenue.upcoming_events_count} upcoming` : ""}. Deleting it would take it off those records.
                      </>
                    )}
                  </span>
                </p>
                {deletingVenue.is_active ? (
                  <p>Archive it instead: it won't be offered when picking a venue, but stays on its events and still counts for clash checks.</p>
                ) : (
                  <p>It's already archived, so it isn't offered when picking a venue.</p>
                )}
              </>
            ) : (
              <p>This venue isn't used on any event, so it can be removed for good.</p>
            )}
          </div>
        </Modal>
      )}
    </DashboardLayout>
  );
}
